/**
 * WeeklyItemCardThumb — renders a compact inline preview of an Oracle save's
 * shareable card, embedded inside a Weekly feed item. Tapping opens the
 * ShareCardModal so viewers can re-share the exact same card to Instagram,
 * X, Messages, etc.
 *
 * Only shown when `item.card_data` is a valid renderable payload; the
 * Weekly.jsx caller guards with `isRenderableCardData(item.card_data)`.
 *
 * Also exported: `isRenderableCardData(cd)` — the shared shape guard used to
 * decide whether to render the thumb.
 */
import React, { useMemo, useRef, useState, useEffect } from "react";
import { Share2 } from "lucide-react";
import { ShareCardFront, ShareCardInsight } from "./ShareCard";
import { getTraditionGlyph, renderGlyphForName, resolveTraditionSlug } from "../lib/traditionGlyphs";
import ShareCardModal from "./ShareCardModal";

const THUMB_W = 220;
const THUMB_H = 391; // 9:16

/** Guard — matches backend's `_card_payload_ok`. */
export function isRenderableCardData(cd) {
  if (!cd || typeof cd !== "object") return false;
  const truths = cd.truths;
  if (!Array.isArray(truths) || truths.length < 3) return false;
  if (cd.card_type === "insight") {
    return !!(cd.subject?.name && cd.key_insight);
  }
  // Default → comparison
  return !!(cd.tradition_a?.name && cd.tradition_b?.name && cd.shared_truth);
}

export default function WeeklyItemCardThumb({ item }) {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);
  const [scale, setScale] = useState(THUMB_W / 1080);

  useEffect(() => {
    if (!wrapperRef.current) return;
    const update = () => {
      const w = wrapperRef.current?.clientWidth || THUMB_W;
      setScale(w / 1080);
    };
    update();
    const obs = new ResizeObserver(update);
    obs.observe(wrapperRef.current);
    return () => obs.disconnect();
  }, []);

  const cardProps = useMemo(() => {
    const cd = item.card_data || {};
    const cardType = cd.card_type === "insight" ? "insight" : "comparison";
    const glyphFor = (t) => {
      const slug = (t?.slug && resolveTraditionSlug(t.slug))
        || resolveTraditionSlug(t?.name)
        || resolveTraditionSlug(t?.tag);
      if (slug) {
        const Glyph = getTraditionGlyph(slug);
        return React.createElement(Glyph, { size: 56 });
      }
      return renderGlyphForName(t?.name || t?.tag, { size: 56 });
    };
    const base = { handle: item.user_handle || "", number: "001", cardType };
    if (cardType === "insight") {
      return {
        ...base,
        subject: {
          name: cd.subject?.name || "—",
          tag:  cd.subject?.tag  || "Tradition",
          essence: cd.subject?.essence || "",
          glyph: glyphFor(cd.subject),
        },
        keyInsight: cd.key_insight || "",
        truths: cd.truths || [],
      };
    }
    return {
      ...base,
      traditionA: {
        name: cd.tradition_a?.name || "—",
        tag:  cd.tradition_a?.tag  || "Tradition",
        essence: cd.tradition_a?.essence || "",
        glyph: glyphFor(cd.tradition_a),
      },
      traditionB: {
        name: cd.tradition_b?.name || "—",
        tag:  cd.tradition_b?.tag  || "Tradition",
        essence: cd.tradition_b?.essence || "",
        glyph: glyphFor(cd.tradition_b),
      },
      sharedTruth: cd.shared_truth || "",
    };
  }, [item]);

  return (
    <>
      <button
        type="button"
        data-testid={`weekly-card-thumb-${item.id}`}
        onClick={(e) => { e.stopPropagation(); setOpen(true); }}
        className="group relative rounded-xl overflow-hidden bg-[hsl(40_30%_92%)] border border-[hsl(35_15%_85%)] shadow-[0_4px_16px_rgba(120,80,30,0.18)] hover:shadow-[0_10px_28px_rgba(120,80,30,0.35)] hover:-translate-y-0.5 transition-all duration-300 block"
        style={{ width: THUMB_W, height: THUMB_H }}
        aria-label="Open share card"
      >
        <div ref={wrapperRef} className="absolute inset-0 overflow-hidden">
          <div style={{
            width: 1080, height: 1920,
            transform: `scale(${scale})`,
            transformOrigin: "top left",
            pointerEvents: "none",
          }}>
            {cardProps.cardType === "insight" ? (
              <ShareCardInsight {...cardProps} width={1080} height={1920} />
            ) : (
              <ShareCardFront {...cardProps} width={1080} height={1920} />
            )}
          </div>
        </div>
        <div className="absolute inset-x-0 bottom-0 h-12 bg-gradient-to-t from-black/45 to-transparent" />
        <div className="absolute bottom-2 right-2 inline-flex items-center gap-1 bg-black/40 backdrop-blur px-2 py-1 rounded-full text-white text-[10px] mono uppercase tracking-[0.18em]">
          <Share2 size={9} />
          Share
        </div>
      </button>

      {open && (
        <ShareCardModal
          open={open}
          onClose={() => setOpen(false)}
          question=""
          response=""
          saveId={item.id}
        />
      )}
    </>
  );
}
